import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';
import { AngularFireStorage } from '@angular/fire/compat/storage';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { ToastrService } from 'ngx-toastr';

import { ReservationFacility } from '../firestore-models/reservation-facility';
import { ReservationDownloadFile } from '../firestore-models/reservation-download-file';
import { ApiReservationDownloadFile, ApiCreateReservationDownloadFilesRequest } from '../api-client-module';
import { ProjectLanguage } from '../firestore-models/project-language';

import { ApiClientService } from '../api-client.service';
import { ConfigurationService } from '../configuration.service';
import { SpinnerService } from '../services/spinner.service';
import { AppConst } from '../app-const';

import { ReservationDownloadDialogComponent } from './reservation-download-dialog/reservation-download-dialog.component';
import { MessageDialogComponent } from '../common/message-dialog/message-dialog.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-reservation-download',
  templateUrl: './reservation-download.component.html',
  styleUrls: ['./reservation-download.component.css'],
  standalone: false,
})
export class ReservationDownloadComponent implements OnInit {

  private reservationFacilitiesCollection!: AngularFirestoreCollection<ReservationFacility>;
  reservationFacilitiesObservable!: Observable<ReservationFacility[]>;
  reservationFacilitiesSubscribe!: Subscription;
  reservationFacilities!: Array<ReservationFacility>;
  //ReservationDownloadFileコレクション
  private reservationDownloadFilesCollection!: AngularFirestoreCollection<ReservationDownloadFile>;
  reservationDownloadFilesObservable!: Observable<ReservationDownloadFile[]>;
  reservationDownloadFilesSubscribe!: Subscription;
  //プロジェクトID
  projectId: string;

  //予約ダウンロードファイル情報テーブル
  displayedColumns: string[] = ['position', 'edit', 'select', 'created', 'reservation_facility_name', 'memo', 'start_date', 'end_date', 'count', 'download'];
  dataSource = new MatTableDataSource<ReservationDownloadFile>();
  selection = new SelectionModel<ReservationDownloadFile>(true, []);
  element!: HTMLElement;

  reservationFacilityCollectionName = AppConst.FIRESTORE_RESERVATION_FACILITIES;

  //各予約設備情報に紐付くプロジェクトの言語情報
  projectLanguages!: Array<ProjectLanguage>;

  projectBaseLang: string = 'ja';

  @ViewChild(MatPaginator, {static: true}) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  constructor(
    public dialog: MatDialog,
    private angularFirestore: AngularFirestore,
    private angularFireStorage: AngularFireStorage,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private configurationService: ConfigurationService,
    private translate: TranslateService
  ) {
    this.projectId = this.configurationService.getProjectId();
  }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
    // 予約設備取得
    this.reservationFacilitiesCollection = this.angularFirestore.collection<ReservationFacility>(
      AppConst.FIRESTORE_RESERVATION_FACILITIES, ref => ref.where('project_id', '==', this.projectId).orderBy('reservation_facility_id', 'asc'));
    this.reservationFacilitiesObservable = this.reservationFacilitiesCollection.valueChanges({ idField: 'id' });
    this.reservationFacilitiesSubscribe = this.reservationFacilitiesObservable.subscribe(reservationFacilities => {
      this.reservationFacilities = reservationFacilities;
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
      this.reservationDownloadFilesCollection = this.angularFirestore.collection<ReservationDownloadFile>(
        AppConst.FIRESTORE_RESERVATION_DOWNLOAD_FILES, ref => ref.where('project_id', '==', this.projectId).orderBy('created', 'desc'));
      this.reservationDownloadFilesObservable = this.reservationDownloadFilesCollection.valueChanges({ idField: 'id' });
      this.reservationDownloadFilesSubscribe = this.reservationDownloadFilesObservable.subscribe(reservationDownloadFiles => {
        this.dataSource.data = this.addFilterValue(reservationDownloadFiles);
      });
    });
  }

  ngOnDestroy(): void {
    this.reservationDownloadFilesSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
    this.selection.clear() :
    this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: ReservationDownloadFile): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  //編集ダイアログ
  openEditDialog(reservationDownloadFile: ReservationDownloadFile): void {
    const startDate: any = reservationDownloadFile!.start_date ? new Date(reservationDownloadFile!.start_date.seconds * 1000) : null;
    const endDate: any = reservationDownloadFile!.end_date ? new Date(reservationDownloadFile!.end_date.seconds * 1000) : null;
    let apiReservationDownloadFile: ApiReservationDownloadFile = {
      id: reservationDownloadFile.id,
      start_date: startDate,
      end_date: endDate,
      reservation_facility_id: reservationDownloadFile.reservation_facility_id,
      memo: reservationDownloadFile.memo,
      project_id: reservationDownloadFile.project_id,
      enabled: reservationDownloadFile.enabled
    };
    const dialogRef = this.dialog.open(ReservationDownloadDialogComponent, {
      width: '50%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_UPDATE, reservationDownloadFile: apiReservationDownloadFile, reservationFacilities: this.reservationFacilities }
    });
  }

  //追加ダイアログ
  openAddDialog(): void {
    const dialogRef = this.dialog.open(ReservationDownloadDialogComponent, {
      width: '50%',
      disableClose: true,
      data: { change_type: AppConst.DATA_CHANGE_TYPE_ADD, reservationDownloadFile: null, reservationFacilities: this.reservationFacilities }
    });
  }
  
  //削除
  deleteData(): void{
    const dialogRef = this.dialog.open(MessageDialogComponent, {
      width: '420px',
      height: '180px',
      disableClose: true,
      data: { message: this.translate.instant('選択されたデータを削除します。よろしいですか？') }
    });

    dialogRef.afterClosed().subscribe(result => {
      let selectedReservationDownloadFiles: Array<ApiReservationDownloadFile> = new Array<ApiReservationDownloadFile>();
      for (const row of this.dataSource.data) {
        if (this.selection.isSelected(row)) {
          const selectedReservationDownloadFile: ApiReservationDownloadFile = {
            id: row.id,
            start_date: row.start_date.toDate(),
            end_date: row.end_date.toDate(),
            reservation_facility_id: row.reservation_facility_id,
            memo: row.memo,
            project_id: row.project_id,
            enabled: row.enabled
          };
          selectedReservationDownloadFiles.push(selectedReservationDownloadFile);
        }
      }
      if (!!result) {
        let request: ApiCreateReservationDownloadFilesRequest = {
          access_token: '',
          change_type: AppConst.DATA_CHANGE_TYPE_DELETE,
          reservation_download_files: selectedReservationDownloadFiles
        }
        this.spinnerService.openSpinner();
        this.apiClientService.createReservationDownloadFiles(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('削除しました。'));
            } else {
              this.toastr.error(this.translate.instant('削除できませんでした。'));
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('削除できませんでした。'));
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
            this.selection.clear();
          }
        );
      }
    });
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }
  
    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'memo':
          return compare(a.memo, b.memo, isAsc);
        case 'count':
          return compare(a.row_count, b.row_count, isAsc);
        default:
          return 0;
      }
    });
  }

  addFilterValue(docs: Array<any>) :Array<any> {
    for (const doc of docs) {
      let filterValue = '';
      filterValue += doc.memo + ' '
      doc['filterValue'] = filterValue;
    }
    return docs;
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  download(reservationDownloadFile: ReservationDownloadFile): void {
    const path = `reservation/${reservationDownloadFile.project_id}/${reservationDownloadFile.file_name}`;
    const storageRef = this.angularFireStorage.ref(path);
    const url = storageRef.getDownloadURL().subscribe((url) => {
      const a = document.createElement("a");
      document.body.appendChild(a);
      a.download = reservationDownloadFile.file_name!;
      a.href = url!;
      a.click();
      document.body.removeChild(a);
    });
  }

}
